import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'core/store/hooks';
import { format } from 'date-fns';
import { RptAgendaCommercialeDto, RptOpportunityDto } from '@api/client';
import { loadActivitiesForOpportunity } from '../../../agenda-actions/_redux/actions';
import { esitoLabel } from '../../../agenda-actions/_utils/esitoLabel';
import AgendaActionDetailModal from '../../../agenda-actions/_components/agenda-action-detail-modal';

interface Props {
  show: boolean;
  setShow: (v: boolean) => void;
  opportunity: RptOpportunityDto | undefined;
}

const ESITO_STYLE: Record<string, { bg: string; color: string }> = {
  POSITIVO:  { bg: '#4caf50', color: '#fff' },
  NEGATIVO:  { bg: '#ef4444', color: '#fff' },
  NEUTRO:    { bg: '#f59e0b', color: '#fff' },
  ANNULLATA: { bg: '#6c757d', color: '#fff' },
};

const OpportunityActivitiesModal: React.FC<Props> = ({ show, setShow, opportunity }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<RptAgendaCommercialeDto[]>([]);
  const [detailData, setDetailData] = useState<RptAgendaCommercialeDto | undefined>(undefined);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const openDetail = (item: RptAgendaCommercialeDto) => {
    setDetailData(item);
    setShowDetailModal(true);
  };

  useEffect(() => {
    if (!show || !opportunity?.pkOpportunity) return;
    setLoading(true);
    dispatch(
      loadActivitiesForOpportunity({
        agendaCommercialeSearchDto: {
          fkCliente: opportunity.fkCliente,
          paging: { page: 0, size: 9999 },
        },
      })
    )
      .unwrap()
      .then((res) => {
        const all = res?.content || [];
        const linked = all.filter((a) => a.opportunityId === opportunity.pkOpportunity);
        linked.sort((a, b) => {
          const da = a.actionDate ? new Date(a.actionDate).getTime() : 0;
          const db = b.actionDate ? new Date(b.actionDate).getTime() : 0;
          return db - da;
        });
        setActivities(linked);
      })
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  }, [show, opportunity]);

  return (
    <Modal show={show} onHide={() => setShow(false)} size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="title-modal-s" style={{ fontSize: '1em' }}>
          {t('Attività collegate')}
          {opportunity?.denominazioneOpportunity && (
            <span className="text-muted ms-2" style={{ fontSize: '0.85em', fontWeight: 400 }}>
              — {opportunity.denominazioneOpportunity}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <Spin />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center text-muted py-4">
            {t('Nessuna attività collegata a questa opportunity')}
          </div>
        ) : (
          <div className="table-responsive table-bordered">
            <table className="table mt-0 mb-0">
              <thead className="bg-red">
                <tr>
                  <th>{t('Cliente')}</th>
                  <th>{t('Referente')}</th>
                  <th>{t('Data azione')}</th>
                  <th>{t('Modalità contatto')}</th>
                  <th>{t('Esito')}</th>
                  <th>{t('Nota')}</th>
                </tr>
              </thead>
              <tbody>
                {activities.map((a, i) => {
                  const esitoStyle = a.esito ? ESITO_STYLE[a.esito] : undefined;
                  return (
                    <tr
                      key={i}
                      style={{ cursor: 'pointer' }}
                      onClick={() => openDetail(a)}
                    >
                      <td>{a.nomeAccount || a.clienteId || '—'}</td>
                      <td>{a.nominativoCliente || '—'}</td>
                      <td>{a.actionDate ? format(new Date(a.actionDate), 'dd/MM/yyyy') : '—'}</td>
                      <td>{a.modalitaContatto || '—'}</td>
                      <td>
                        {esitoStyle ? (
                          <span
                            style={{
                              backgroundColor: esitoStyle.bg,
                              color: esitoStyle.color,
                              borderRadius: '12px',
                              padding: '2px 10px',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {esitoLabel(a.esito)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ maxWidth: 280 }}>
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.nota || '—'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>

      <AgendaActionDetailModal
        show={showDetailModal}
        setShow={setShowDetailModal}
        data={detailData}
      />
    </Modal>
  );
};

export default OpportunityActivitiesModal;
